import { useState } from 'react';
import { useForm } from '@mantine/form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  Grid,
  Text,
  TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import {
  useSaveMutation,
} from '@/services/api';
import routes from '@/routes';

import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { notifications } from '@mantine/notifications';
import { IconFileTypePdf } from '@tabler/icons-react';
import { BANKS } from '@/data/constants/banks';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { formatNumberWithCommas } from '@/helpers/string';
import { removeNumberFormatting } from '@/helpers/number';
import ProcessButton from '@/components/ProcessButton';
import DocumentIcon from '@/assets/images/document.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './existBankLoan.module.css';

const UploadBankFile = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const proposal = useSelector((state) => state.proposal);
  const { extraDetails } = proposal;
  const [apiSave] = useSaveMutation();
  const { id } = proposal;
  useIdInStoreGuard();

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      bank: extraDetails?.bank || '',
      currentLoanAmount: formatNumberWithCommas(extraDetails?.currentLoanAmount) || '',
    },
  });

  // Get selected bank data
  const getSelectedBankData = () => {
    if (!form.values.bank) return null;
    return BANKS.find((bank) => bank.value === form.values.bank);
  };

  const handleNumberChange = (field) => (event) => {
    const formattedValue = formatNumberWithCommas(event.target.value);
    form.setFieldValue(field, formattedValue);
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'bankingConduct')?.path, { id }));
  };

  const handleSubmit = async () => {
    if (!form.values.bank || !form.values.currentLoanAmount) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('אנא בחר בנק והזן סכום הלוואה נוכחי'),
      });
      return;
    }

    setLoading(true);
    try {
      const currentLoanAmountNumber = removeNumberFormatting(form.values.currentLoanAmount);

      // Save to database
      await apiSave({
        id,
        currentLoanAmount: currentLoanAmountNumber,
      });

      // Update Redux store
      dispatch(updateExtraDetails(
        {
          currentLoanAmount: currentLoanAmountNumber,
          bank: form.values.bank,
        },
      ));

      // Navigate to mortgage cycle action page
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loanAmount').path, { id }));
    } catch (error) {
      console.error('Error saving current loan amount:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProcessHeader
      headerText={t('pages.mortgageCycle.existBankLoan.title')}
      headerColor="#FFF6D1"
      headerTextColor="#DB6E00"
      subtitle={t('pages.mortgageCycle.existBankLoan.subtitle')}
      showReturnButton
      onReturn={handleReturn}
      icon={DocumentIcon}
    >
      <Page.Section>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              label={t('pages.mortgageCycle.existBankLoan.bankSelect.label')}
              data={BANKS.map((bank) => ({ value: bank.value, label: bank.name }))}
              value={form.values.bank}
              onChange={(value) => form.setFieldValue('bank', value)}
              required
              mb="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label={t('pages.mortgageCycle.existBankLoan.currentLoanAmount.label')}
              placeholder={t('pages.mortgageCycle.existBankLoan.currentLoanAmount.placeholder')}
              value={form.values.currentLoanAmount}
              onChange={handleNumberChange('currentLoanAmount')}
              required
              mb="md"
            />
          </Grid.Col>
        </Grid>
      </Page.Section>

      <Page.Section
        withCollapse
        withBackground
        startClose
        onCollapseChange={() => { }}
        title={t('pages.mortgageCycle.bankFileUpload.sectionTitle')}
      >
        {/* General PDF Download - Always Available */}
        <div className={classes.pdfDownloadContainer}>
          <Text className={classes.pdfDownloadLabel} size="sm" c="dimmed">
            {t('pages.mortgageCycle.bankFileUpload.downloadFile')}
          </Text>
          <a
            href="/docs/מחזור-הנחיות דוח יתרות לסילוק קרדיט פרו.pdf"
            download
            target="_blank"
            rel="noopener noreferrer"
            className={classes.pdfDownloadButton}
          >
            <IconFileTypePdf size={18} />
            {t('pages.mortgageCycle.bankFileUpload.downloadButton')}
          </a>
        </div>

        {/* Bank-Specific Information - When Bank is Selected */}
        {form.values.bank && getSelectedBankData() && (
          <div className={classes.bankInfoContainer}>
            <Text className={classes.bankInfoHeader} mb="lg" size="lg" fw={600} c="#495057">
              {t('pages.mortgageCycle.bankFileUpload.bankInfoTitle', { bankName: getSelectedBankData().name })}
            </Text>
            {/* First Row - Website, Phone, Working Hours */}
            <div className={classes.bankInfoRow}>
              {getSelectedBankData().website && (
                <div className={`${classes.bankInfoCard} ${classes.bankInfoCardWebsite}`}>
                  <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                    🌐 {t('pages.mortgageCycle.bankFileUpload.bankWebsite')}
                  </Text>
                  <a
                    href={getSelectedBankData().website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.bankInfoLink}
                  >
                    {getSelectedBankData().website}
                  </a>
                </div>
              )}

              {getSelectedBankData().phone && (
                <div className={`${classes.bankInfoCard} ${classes.bankInfoCardPhone}`}>
                  <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                    📞 {t('pages.mortgageCycle.bankFileUpload.bankPhone')}
                  </Text>
                  <Text className={classes.bankInfoContent} size="md" fw={600} c="#495057">
                    {getSelectedBankData().phone}
                  </Text>
                </div>
              )}

              {getSelectedBankData().workingHours && (
                <div className={`${classes.bankInfoCard} ${classes.bankInfoCardHours}`}>
                  <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                    🕒 {t('pages.mortgageCycle.bankFileUpload.bankWorkingHours')}
                  </Text>
                  <Text className={classes.bankInfoContent} size="md" c="#495057">
                    {getSelectedBankData().workingHours}
                  </Text>
                </div>
              )}
            </div>

            {getSelectedBankData().instructions && (
              <div className={classes.instructionsContainer}>
                <Text className={classes.instructionsTitle} mb="md" size="md" fw={600} c="#495057">
                  📋 {t('pages.mortgageCycle.bankFileUpload.bankInstructions')}
                </Text>
                <ol className={classes.instructionsList}>
                  {getSelectedBankData().instructions.map((instruction, index) => (
                    <li key={index} className={classes.instructionsListItem}>
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </Page.Section>

      <Page.Actions>
        <ProcessButton onClick={handleSubmit} loading={loading} />
      </Page.Actions>
    </ProcessHeader>
  );
};

export default UploadBankFile;
